import { addQueryArgs } from "@wordpress/url";

/**
 * A filter set for fetching detail records.
 * Each key-value pair becomes a query parameter (e.g. { asset_site: "Site A", type_asset: "chromebook" })
 */
export type DetailExportFilterSet = Record<string, string>;

/** Column definition for CSV export */
export interface ExportColumn {
  key: string;
  header: string;
}

interface DetailExportOptions {
  baseUrl: string;
  filterSets: DetailExportFilterSet[];
  columns?: ExportColumn[];
  /** Used as the prefix of the downloaded filename: {exportTitle}-detail-{date}.csv */
  exportTitle?: string;
  /** Optional client-side filter applied after all records are fetched */
  postFilter?: (record: Record<string, any>) => boolean;
  /** Global query params merged into every filter set (e.g. date range filters) */
  globalFilters?: Record<string, string>;
  /** Response parser mode: WP core list endpoint vs site-users custom endpoint */
  responseMode?: "wp" | "site-users";
  /** Optional request init for fetch calls (headers, credentials, etc.) */
  requestInit?: RequestInit;
  /** Optional fetcher override, useful for token-refresh flows */
  fetcher?: (url: string, init?: RequestInit) => Promise<Response>;
  onProgress?: (fetched: number, total: number, setIndex: number, totalSets: number) => void;
}

const DEFAULT_EXPORT_COLUMNS: ExportColumn[] = [
  { key: "title.rendered", header: "Title" },
  { key: "meta_box.serial_number", header: "Serial Number" },
  { key: "meta_box.model", header: "Model" },
  { key: "meta_box.status_asset", header: "Status" },
  { key: "meta_box.type_asset", header: "Type" },
  { key: "meta_box.asset_site", header: "Site" },
  { key: "meta_box.asset_location", header: "Location" },
  { key: "meta_box.purchase_date", header: "Purchase Date" },
  { key: "meta_box.purchased_from", header: "Purchased From" },
  { key: "meta_box.po_number", header: "PO Number" },
  { key: "meta_box.cost", header: "Cost" },
  { key: "meta_box.funding_source", header: "Funding Source" },
  { key: "meta_box.description", header: "Description" },
  { key: "date", header: "Created Date" },
  { key: "modified", header: "Modified Date" },
];

function recordsToCsv(records: Record<string, any>[], customColumns?: ExportColumn[]): string {
  if (records.length === 0) return "";

  const columns = customColumns ?? DEFAULT_EXPORT_COLUMNS;

  // Build header row with line number
  const headerRow = ["#", ...columns.map((c) => escapeCSV(c.header))].join(",");

  // Build data rows with line number
  const dataRows = records.map((record, index) => {
    const values = columns.map((col) => {
      const value = getNestedValue(record, col.key);
      return escapeCSV(value);
    });
    return [index + 1, ...values].join(",");
  });

  return [headerRow, ...dataRows].join("\n");
}

function getNestedValue(obj: any, path: string): string {
  const value = path.split(".").reduce((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    // If acc is a string that looks like JSON, try parsing it before accessing key
    if (typeof acc === "string" && acc.startsWith("{")) {
      try {
        const parsed = JSON.parse(acc);
        return parsed?.[key];
      } catch {
        return undefined;
      }
    }
    return acc[key];
  }, obj as any);
  if (value === null || value === undefined) return "";
  // If the resolved value is an object with a name property, return the name
  if (typeof value === "object" && value.name) return String(value.name);
  // If it's a JSON string representing a term object, extract name
  if (typeof value === "string" && value.startsWith("{")) {
    try {
      const parsed = JSON.parse(value);
      if (parsed?.name) return String(parsed.name);
    } catch {
      // not JSON, return as-is
    }
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function escapeCSV(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Fetches full asset records for multiple filter sets (one per selected row),
 * paginates through each, consolidates into a single CSV, and downloads it.
 *
 * Each filter set represents one selected row from the ExportTable,
 * e.g. { asset_site: "Site A", purchase_date: "2024", type_asset: "chromebook" }
 */
export async function exportDetailAssetsToCsv({
  baseUrl,
  filterSets,
  columns,
  exportTitle,
  postFilter,
  globalFilters,
  responseMode = "wp",
  requestInit,
  fetcher,
  onProgress,
}: DetailExportOptions): Promise<void> {
  const perPage = 100;
  let allRecords: Record<string, any>[] = [];

  for (let setIdx = 0; setIdx < filterSets.length; setIdx++) {
    const filters = filterSets[setIdx];
    let page = 1;
    let setTotal = 0;
    let setFetched = 0;
    let hasMore = true;

    while (hasMore) {
      const url = addQueryArgs(baseUrl, {
        ...globalFilters,
        ...filters,
        per_page: perPage,
        page,
      });
      const response = await (fetcher ? fetcher(url, requestInit) : fetch(url, requestInit));

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      let pageData: Record<string, any>[] = [];

      if (responseMode === "site-users") {
        const payload: any = await response.json();
        pageData = Array.isArray(payload?.items) ? payload.items : [];
        if (page === 1) {
          setTotal = Number(payload?.total ?? 0);
        }
      } else {
        if (page === 1) {
          setTotal = parseInt(response.headers.get("X-WP-Total") || "0");
        }
        const payload: any = await response.json();
        pageData = Array.isArray(payload) ? payload : [];
      }

      allRecords = [...allRecords, ...pageData];
      setFetched += pageData.length;
      onProgress?.(allRecords.length, setTotal, setIdx + 1, filterSets.length);

      if (pageData.length < perPage || setFetched >= setTotal) {
        hasMore = false;
      } else {
        page++;
      }
    }
  }

  // Deduplicate by record ID in case filter sets overlap
  const seen = new Set<string | number>();
  let uniqueRecords = allRecords.filter((record) => {
    const recordId = (record?.id ?? record?.wp_user_id ?? JSON.stringify(record)) as string | number;
    if (seen.has(recordId)) return false;
    seen.add(recordId);
    return true;
  });

  // Apply client-side post-filter if provided
  if (postFilter) {
    uniqueRecords = uniqueRecords.filter(postFilter);
  }

  const csv = recordsToCsv(uniqueRecords, columns);
  const prefix = exportTitle ?? "assets-detail-export";
  downloadCsv(csv, `${prefix}-detail-${new Date().toISOString().slice(0, 10)}.csv`);
}
